// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface GraphicsPackageType {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  is_active: boolean;
  display_order: number;
}

const GraphicsPackageTypesManagementTab: React.FC = () => {
  const [types, setTypes] = useState<GraphicsPackageType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<GraphicsPackageType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    display_order: 0
  });

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await (supabase as any)
        .from('graphics_package_types')
        .select('*')
        .order('display_order');

      if (error) throw error;
      setTypes((data as any) || []);
    } catch (error: any) {
      console.error('Error fetching package types:', error);
      toast.error('Failed to load package types');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (type: GraphicsPackageType) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      slug: type.slug,
      description: type.description || '',
      icon: type.icon || '',
      display_order: type.display_order
    });
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingType(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '',
      display_order: types.length + 1
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    try {
      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-');

      if (editingType) {
        const { error } = await (supabase as any)
          .from('graphics_package_types')
          .update({
            name: formData.name,
            slug,
            description: formData.description,
            icon: formData.icon,
            display_order: formData.display_order
          })
          .eq('id', editingType.id);

        if (error) throw error;
        toast.success('Package type updated successfully');
      } else {
        const { error } = await (supabase as any)
          .from('graphics_package_types')
          .insert({
            name: formData.name,
            slug,
            description: formData.description,
            icon: formData.icon,
            display_order: formData.display_order
          });

        if (error) throw error;
        toast.success('Package type created successfully');
      }

      setShowForm(false);
      fetchTypes();
    } catch (error: any) {
      console.error('Error saving package type:', error);
      toast.error('Failed to save package type');
    }
  };

  const handleDelete = async (type: GraphicsPackageType) => {
    if (!confirm(`Are you sure you want to delete "${type.name}"?`)) return;

    try {
      const { error } = await (supabase as any)
        .from('graphics_package_types')
        .delete()
        .eq('id', type.id);

      if (error) throw error;
      toast.success('Package type deleted successfully');
      fetchTypes();
    } catch (error: any) {
      console.error('Error deleting package type:', error);
      toast.error('Failed to delete package type');
    }
  };

  const toggleActive = async (type: GraphicsPackageType) => {
    try {
      const { error } = await (supabase as any)
        .from('graphics_package_types')
        .update({ is_active: !type.is_active })
        .eq('id', type.id);

      if (error) throw error;
      toast.success(`Package type ${type.is_active ? 'deactivated' : 'activated'}`);
      fetchTypes();
    } catch (error: any) {
      console.error('Error toggling type status:', error);
      toast.error('Failed to update type status');
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-6">Loading package types...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Package Types</h3>
          <p className="text-sm text-muted-foreground">
            Manage graphics package types (Commitment, Offer, Game Day, etc.)
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Type
        </Button>
      </div>

      <div className="grid gap-4">
        {types.map((type) => (
          <Card key={type.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{type.icon}</span>
                  <div>
                    <h4 className="font-semibold text-lg">{type.name}</h4>
                    <p className="text-sm text-muted-foreground">{type.slug}</p>
                  </div>
                  <Badge variant={type.is_active ? "default" : "secondary"}>
                    {type.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => toggleActive(type)}
                  >
                    {type.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(type)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDelete(type)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {type.description && (
                <p className="text-sm text-muted-foreground">{type.description}</p>
              )}
            </CardContent>
          </Card>
        ))}

        {types.length === 0 && (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">No package types found.</p>
              <Button className="mt-4" onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Type
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Edit Package Type' : 'Create Package Type'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Commitment Post"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Slug</label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="e.g., commitment"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Icon (emoji)</label>
              <Input
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="🎓"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Display Order</label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this package type"
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end mt-6">
            <Button variant="outline" onClick={() => setShowForm(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              <Save className="h-4 w-4 mr-2" />
              {editingType ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GraphicsPackageTypesManagementTab;